import { theme } from "antd";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import PhoneInput from "react-phone-number-input";
import flags from "country-flag-icons/react/3x2";
import "react-phone-number-input/style.css";

/**
 * PhoneNumberInput — champ téléphone avec sélecteur de préfixe international
 * (drapeau + nom du pays + indicatif), bâti sur `react-phone-number-input`
 * (métadonnées Google `libphonenumber-js`, drapeaux SVG `country-flag-icons`
 * embarqués, sans appel réseau).
 *
 * Compatible antd `Form.Item` : reçoit `value` / `onChange` et échange une
 * chaîne unique au format E.164, ex. "+213555123456" (indicatif + numéro).
 * Pays par défaut : Algérie (🇩🇿 +213), l'indicatif reste visible et non
 * éditable ; l'utilisateur change le pays via la liste.
 *
 * La validation du format est fournie par `buildPhoneRule` (utils/phone).
 */

// Habillage de l'input de la librairie pour l'aligner sur un <Input> antd.
// Les couleurs viennent des design tokens antd passés en prop `tokens`
// (thème clair / sombre). `tokens` n'est pas propagé au DOM : emotion filtre
// les props non-HTML des composants `styled('div')`.
const PhoneInputWrapper = styled("div")(({ tokens }) => ({
  "& .PhoneInput": {
    display: "flex",
    alignItems: "stretch",
    minHeight: tokens.controlHeight,
    border: `1px solid ${tokens.colorBorder}`,
    borderRadius: tokens.borderRadius,
    background: tokens.colorBgContainer,
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  "& .PhoneInput--focus": {
    borderColor: tokens.colorPrimary,
    boxShadow: `0 0 0 2px ${tokens.controlOutline}`,
  },
  "& .PhoneInput--disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  "& .PhoneInputCountry": {
    margin: 0,
    padding: "0 11px",
    borderRight: `1px solid ${tokens.colorBorder}`,
  },
  "& .PhoneInputInput": {
    height: "auto",
    padding: "4px 11px",
    border: "none",
    outline: "none",
    background: "transparent",
    color: tokens.colorText,
    fontSize: 14,
    lineHeight: 1.5714,
    borderRadius: `0 ${tokens.borderRadius}px ${tokens.borderRadius}px 0`,
  },
  "& .PhoneInputInput::placeholder": {
    color: tokens.colorTextPlaceholder,
  },
  '[dir="rtl"] & .PhoneInputCountry': {
    borderRight: "none",
    borderLeft: `1px solid ${tokens.colorBorder}`,
  },
}));

const PhoneNumberInput = ({
  value = "",
  onChange,
  disabled,
  placeholder,
}) => {
  const { token } = theme.useToken();

  return (
    <PhoneInputWrapper tokens={token}>
      <PhoneInput
        flags={flags}
        international
        defaultCountry="DZ"
        countryCallingCodeEditable={false}
        addInternationalOption={false}
        limitMaxLength
        value={value || undefined}
        onChange={(next) => onChange?.(next || "")}
        disabled={disabled}
        placeholder={placeholder}
        numberInputProps={{ inputMode: "tel" }}
      />
    </PhoneInputWrapper>
  );
};

PhoneNumberInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default PhoneNumberInput;
